<template>
  <section v-if="filteredStories.length" class="stories">
    <ul class="stories__thumbail-list">
      <li class="stories__thumbail-item" v-for="(story, i) of filteredStories" :key="i">
        <story-thumbnail
          :title="story.title"
          :text-color="story.textColor"
          :bg-color="story.bgColor"
          :img="story.img"
          :viewed="viewedFlagMap[story.id].viewed"
          @click="selectStory(i)"
        />
      </li>
    </ul>
    <div v-if="isModalOpened" class="stories__vail" @click.self="closeModal">
      <ul class="stories__card-list">
        <li class="stories__card-item" v-for="(story, i) of filteredStories" :key="story.id">
          <story-card
            v-if="hasStorySlides(story)"
            :current-story-idx="i"
            :active-story-idx="actStoryIdx"
            :active-slide-idx="actSlideIdx"
            :is-paused="isPausedSlide"
            :hide-prev-btn="hidePrevBtn"
            :hide-next-btn="hideNextBtn"
            :is-stale-hold="isStaleHold"
            :story="story"
            @slide:prev="prevSlide"
            @timer:start="startTimer"
            @slide:pause="pauseSlide"
            @slide:continue="continueSlide"
            @slide:next="nextSlide"
            @story:select="selectStory"
            @story:next="nextStory(true)"
            @story:prev="prevStory(true, true)"
            @story:close="closeModal"
            @set:stale-hold-state="setStaleHoldState"
          />
        </li>
      </ul>
    </div>
  </section>
</template>

<script>
import StoryThumbnail from './StoryThumbnail.vue'
import StoryCard from './StoryCard.vue'
import { SLIDE_DURATION_MS } from '@base/stories/const'
import { createPauseableTimeout } from '@shared/utils/timeout'
import StoriesStorage from '@base/stories/stories.storage'

export default {
  name: 'ui-stories',
  components: {
    'story-thumbnail': StoryThumbnail,
    'story-card': StoryCard,
  },
  props: {
    stories: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      actStoryIdx: null,
      actSlideIdx: 0,
      isModalOpened: false,
      isModalClicked: false,
      isPausedSlide: true,
      isStaleHold: false,
      timeout: createPauseableTimeout(this.nextSlide, SLIDE_DURATION_MS),
      storiesStorage: StoriesStorage,
      viewedFlagMap: {},
    }
  },
  created() {
    this.storiesStorage.setup(this.filteredStories)
    this.viewedFlagMap = this.storiesStorage.viewedFlagMap
  },
  computed: {
    filteredStories() {
      return this.stories?.filter(story => !!story?.slides?.length) ?? []
    },
    actStory() {
      return this.filteredStories?.[this.actStoryIdx] ?? {}
    },
    actSlide() {
      return this.actSlides?.[this.actSlideIdx] ?? {}
    },
    actSlides() {
      return this.actStory?.slides ?? []
    },
    hidePrevBtn() {
      return this.isFirstStory() && this.isFirstSlide()
    },
    hideNextBtn() {
      return this.isLastStory() && this.isLastSlide()
    },
  },
  methods: {
    setViewedSlide() {
      this.storiesStorage.setViewedSlide(this.actStory.id, this.actSlideIdx)
    },
    pauseSlide() {
      this.isPausedSlide = true
      this.timeout.pause()
    },
    resetSlide() {
      this.isPausedSlide = true
      this.timeout.reset()
    },
    startSlide() {
      this.isPausedSlide = false
      this.timeout.start()
    },
    continueSlide() {
      this.isPausedSlide = false
      this.timeout.continue()
    },
    togglePauseSlide() {
      if (this.isPausedSlide) this.continueSlide()
      else this.pauseSlide()
    },
    prevSlide() {
      if (this.isFirstSlide()) {
        if (this.isFirstStory()) this.closeModal()
        else {
          this.isPausedSlide = true
          this.prevStory(false, false)
          this.actSlideIdx = this.actSlides.length - 1
          this.setViewedSlide()
        }
      } else {
        this.isPausedSlide = true
        this.actSlideIdx = this.actSlideIdx - 1
        this.setViewedSlide()
      }
    },
    nextSlide() {
      if (this.isLastSlide()) {
        if (this.isLastStory()) this.closeModal()
        else {
          this.isPausedSlide = true
          this.nextStory(false)
          this.actSlideIdx = 0
          this.setViewedSlide()
        }
      } else {
        this.setViewedSlide()
        this.isPausedSlide = true
        this.actSlideIdx = this.actSlideIdx + 1
        this.setViewedSlide()
      }
    },
    isFirstSlide() {
      return this.actSlideIdx === 0
    },
    isLastSlide() {
      return this.actSlideIdx === this.actSlides.length - 1
    },
    selectStory(storyIdx) {
      if (this.isActStory(storyIdx)) this.nextSlide()
      else {
        this.isPausedSlide = true
        this.actStoryIdx = storyIdx
        this.actSlideIdx = 0
        this.setViewedSlide()

        if (!this.isModalOpened) {
          if (this.hasStorySlides(this.actStory)) this.isModalOpened = true
          else console.error(`Error opening story: story with id = ${this.actStory?.id} have no slides`)
        }
      }
    },
    prevStory(needSetStartSlide = true, setPauseFlag = true) {
      if (this.isFirstStory()) this.closeModal()
      else {
        if (setPauseFlag) this.isPausedSlide = true
        this.actStoryIdx = this.actStoryIdx - 1
        if (needSetStartSlide) {
          this.actSlideIdx = 0
          this.setViewedSlide()
        }
      }
    },
    nextStory(setPauseFlag = true) {
      if (this.isLastStory()) this.closeModal()
      else {
        if (setPauseFlag) this.isPausedSlide = true
        this.actStoryIdx = this.actStoryIdx + 1
        this.actSlideIdx = 0
        this.setViewedSlide()
      }
    },
    hasStorySlides(story) {
      return !!story?.slides?.length
    },
    isActStory(currStoryIdx) {
      return this.actStoryIdx === currStoryIdx
    },
    isFirstStory() {
      return this.actStoryIdx === 0
    },
    isLastStory() {
      return this.actStoryIdx === this.filteredStories?.length - 1
    },
    closeModal() {
      this.resetSlide()
      this.isModalOpened = false
      this.isModalClicked = false
      this.actStoryIdx = null
    },
    startTimer(duration = SLIDE_DURATION_MS) {
      this.isPausedSlide = false
      this.timeout.start(duration)
    },
    setStaleHoldState(actState) {
      this.isStaleHold = actState
    },
    onKey(event) {
      switch (event.code) {
        case 'Escape':
          event.preventDefault()
          this.closeModal()
          break
        case 'ArrowLeft':
          event.preventDefault()
          this.prevSlide()
          break
        case 'ArrowRight':
          event.preventDefault()
          this.nextSlide()
          break
        case 'Space':
          event.preventDefault()
          this.togglePauseSlide()
      }
    },
  },
  watch: {
    isModalOpened(val) {
      document.documentElement.classList.toggle('no-scroll', val)
      if (val) {
        window.addEventListener('keydown', this.onKey)
      } else {
        window.removeEventListener('keydown', this.onKey)
      }
    },
  },
}
</script>

<style lang="sass">
@import "~@src/legacy/sass/params"

.stories
  ul
    list-style: none

  &__thumbail-list
    color: #0a0a0a
    display: flex
    gap: 22px
    padding: 14px 0
    overflow-x: auto
    -ms-overflow-style: none
    scrollbar-width: none

    &::-webkit-scrollbar
      display: none

    @media screen and (max-width: $mobile-size)
      scroll-snap-type: x mandatory
      scroll-behavior: smooth

  &__thumbail-item
    --ml-first-child: 6px
    --mr-last-child: 6px

    min-width: 207px
    width: calc(25% - 19px)

    @media screen and (max-width: 980px)
      --ml-first-child: 38px
      --mr-last-child: 6px

      scroll-snap-align: center

    @media screen and (max-width: 920px)
      --ml-first-child: 14px
      --mr-last-child: 14px

    &:first-child
      margin-left: var(--ml-first-child)

    &:last-child
      margin-right: var(--mr-last-child)

  &__vail
    position: fixed
    inset: 0
    background: rgba(0, 0, 0, .7)
    z-index: 20
</style>



// WEBPACK FOOTER //
// src/templates/components/base/stories/index.vue